import React, { useEffect, useRef, useState } from 'react';
import './CourseHeroVisual.css';

const CHART_PATH =
  'M4,70 C18,66 26,58 38,54 C50,50 58,56 70,46 C82,36 90,40 102,30 C114,20 124,24 136,16 C148,10 156,12 168,8';

const LINES = [
  'Claude: scan SPX names with unusual volume',
  'Codex: draft the trade checklist',
  'BotSpot: open broker review for approval',
  'Agent: watch the book overnight',
];

/** Markets covered in the cohort. Names only, never invented prices. */
const TICKER_ITEMS = [
  'Stocks', 'ETFs', 'Options', 'Crypto', 'Futures', 'Prediction markets',
];

/** Volume-style bars under the equity path. Heights are decorative. */
const BARS = [30, 46, 38, 58, 44, 66, 52, 74, 60, 82, 70, 92];

const TYPE_MS = 22;
const LINE_PAUSE_MS = 300;
const TICKET_MS = 1300;   // ticket visible before it self-approves
const APPROVE_MS = 900;   // approved state before the chart draws
const HOLD_MS = 5200;     // full scene hold before the loop restarts
const PL_TARGET = 2.4;    // illustrative percent, counted up on screen

/**
 * Animated hero visual for the live bootcamp sales page.
 * Plays the actual cohort story in a loop:
 *   1. AI commands type out and check off,
 *   2. a reviewed trade ticket slides in and is approved (human in command),
 *   3. the equity path draws with volume bars, a live dot, and a counting P/L.
 * A market ticker scrolls under the header. No people, no fake prices.
 * Respects prefers-reduced-motion (renders the finished scene, still).
 */
const CourseHeroVisual = () => {
  // line = index of the line currently typing; lines below it are complete.
  const [typed, setTyped] = useState({ line: 0, chars: 0 });
  // 'type' -> 'ticket' -> 'approved' -> 'chart' -> loop
  const [phase, setPhase] = useState('type');
  const [pl, setPl] = useState(0);
  const [reduced, setReduced] = useState(false);
  const plTimer = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(Boolean(mq?.matches));
    apply();
    mq?.addEventListener?.('change', apply);
    return () => mq?.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    if (reduced) {
      setTyped({ line: LINES.length, chars: 0 });
      setPhase('chart');
      setPl(PL_TARGET);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const schedule = (fn, ms) => {
      if (cancelled) return;
      timer = setTimeout(fn, ms);
    };
    const typeLine = (lineIndex, charIndex) => {
      if (cancelled) return;
      if (lineIndex >= LINES.length) {
        setTyped({ line: LINES.length, chars: 0 });
        setPhase('ticket');
        schedule(() => {
          setPhase('approved');
          schedule(() => {
            setPhase('chart');
            schedule(() => {
              // Reset the whole scene and run it again.
              setPhase('type');
              setPl(0);
              setTyped({ line: 0, chars: 0 });
              schedule(() => typeLine(0, 0), 600);
            }, HOLD_MS);
          }, APPROVE_MS);
        }, TICKET_MS);
        return;
      }
      const text = LINES[lineIndex];
      if (charIndex > text.length) {
        setTyped({ line: lineIndex + 1, chars: 0 });
        schedule(() => typeLine(lineIndex + 1, 0), LINE_PAUSE_MS);
        return;
      }
      setTyped({ line: lineIndex, chars: charIndex });
      schedule(() => typeLine(lineIndex, charIndex + 1), TYPE_MS);
    };
    schedule(() => typeLine(0, 0), 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [reduced]);

  // Count the P/L up once the chart phase starts (about 1.2s to target).
  useEffect(() => {
    if (reduced) return undefined;
    clearInterval(plTimer.current);
    if (phase !== 'chart') return undefined;
    const startedAt = Date.now();
    plTimer.current = setInterval(() => {
      const t = Math.min((Date.now() - startedAt) / 1200, 1);
      // ease-out so the number lands softly instead of snapping.
      const eased = 1 - (1 - t) * (1 - t);
      setPl(Math.round(PL_TARGET * eased * 10) / 10);
      if (t >= 1) clearInterval(plTimer.current);
    }, 50);
    return () => clearInterval(plTimer.current);
  }, [phase, reduced]);

  const step = typed.line;
  const ticketIn = phase !== 'type';
  const approved = phase === 'approved' || phase === 'chart';
  const chartOn = phase === 'chart';

  return (
    <div className="cp-hero-visual" data-testid="course-hero-visual" aria-label="Animated AI trading workspace demo">
      <div className="cp-hero-visual-head">
        <span className="cp-hero-visual-title">AI trading workspace</span>
        <span className={`cp-hero-visual-pill ${chartOn ? 'is-on' : ''}`}>
          <span className="cp-hero-visual-pill-dot" />
          {chartOn ? 'LIVE · WATCHING' : 'BUILDING'}
        </span>
      </div>

      <div className="cp-hv-ticker" aria-hidden="true">
        <div className="cp-hv-ticker-track">
          <div className="cp-hv-ticker-set">
            {TICKER_ITEMS.map((item) => <span key={item}>{item}</span>)}
          </div>
          <div className="cp-hv-ticker-set">
            {TICKER_ITEMS.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>
      </div>

      <ul className="cp-hero-visual-lines">
        {LINES.map((text, index) => {
          const isDone = step > index;
          const isTyping = !reduced && step === index;
          return (
            <li key={text} className={isDone || isTyping ? 'is-in' : ''}>
              <span className={`cp-hero-visual-check ${isDone ? 'is-done' : ''}`} aria-hidden="true" />
              <span className="cp-hv-line">
                <span className="cp-hv-ghost">{text}</span>
                <span className="cp-hv-typed" aria-hidden="true">
                  {isDone ? text : (isTyping ? text.slice(0, typed.chars) : '')}
                  {isTyping ? <span className="cp-hv-caret" /> : null}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      {/* The command-seat moment: the AI prepared a trade, the human approves
          it. Purely illustrative, so it is one row and never interactive. */}
      <div
        className={`cp-hv-ticket ${ticketIn ? 'is-in' : ''} ${approved ? 'is-approved' : ''}`}
        aria-hidden="true"
      >
        <span className="cp-hv-ticket-tag">{approved ? 'SENT' : 'REVIEW'}</span>
        <span className="cp-hv-ticket-body">Reviewed trade · SPY covered call</span>
        <span className="cp-hv-approve">
          {approved ? '✓ Approved' : 'Approve'}
        </span>
      </div>

      <div className={`cp-hero-visual-chart ${chartOn ? 'is-on' : ''}`}>
        <svg viewBox="0 0 172 78" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="cpHeroChartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(217,119,6,0.35)" />
              <stop offset="100%" stopColor="rgba(217,119,6,0)" />
            </linearGradient>
          </defs>
          {[24, 44, 64].map((y) => (
            <line key={y} className="cp-hv-gridline" x1="4" y1={y} x2="168" y2={y} />
          ))}
          <g className={`cp-hv-bars ${chartOn ? 'is-on' : ''}`}>
            {BARS.map((h, i) => (
              <rect
                key={i}
                className="cp-hv-bar"
                x={8 + i * 13.4}
                y={78 - (h * 0.28)}
                width="6"
                height={h * 0.28}
                style={{ transitionDelay: `${i * 55}ms` }}
              />
            ))}
          </g>
          <path
            className={`cp-hero-visual-area ${chartOn ? 'is-draw' : ''}`}
            d={`${CHART_PATH} L168,78 L4,78 Z`}
            fill="url(#cpHeroChartFill)"
          />
          <path
            className={`cp-hero-visual-line ${chartOn ? 'is-draw' : ''}`}
            d={CHART_PATH}
          />
        </svg>
        {/* HTML dot: the SVG is stretched (preserveAspectRatio none), which
            would deform a <circle> into an ellipse. */}
        <span className={`cp-hv-dot ${chartOn ? 'is-on' : ''}`} aria-hidden="true" />
        <div className="cp-hero-visual-chart-meta">
          <span>Reviewed trade path</span>
          <strong className={chartOn ? 'is-on' : ''}>
            {chartOn ? `+${pl.toFixed(1)}%` : '…'}
          </strong>
        </div>
      </div>

      <p className="cp-hero-visual-foot">Illustrative demo of the live cohort workflow.</p>
    </div>
  );
};

export default CourseHeroVisual;
